import { fetchJSON as fetchAPI } from './client';

export type MissionAttribute = {
  key: string;
  name: string;
  description: string;
};

export type PhilosophyPrinciple = {
  key: string;
  name: string;
  description: string;
  direction: string;
};

export type VisualIdentityRule = {
  key: string;
  category: string;
  name: string;
  value?: string;
  description?: string;
};

export type ComponentLibraryEntry = {
  key: string;
  name: string;
  category: string;
  description?: string;
  mandatory: boolean;
  figma_ref?: string;
  react_path?: string;
};

export type DashboardPhilosophyRule = {
  key: string;
  question: string;
  description: string;
  priority: number;
};

export type WorkspaceRegion = {
  key: string;
  name: string;
  description: string;
  order: number;
  required: boolean;
};

export type NavigationPattern = {
  key: string;
  name: string;
  scope: string;
  description: string;
};

export type AIExperiencePattern = {
  key: string;
  name: string;
  interface_type: string;
  reasoning_visible: boolean;
  description: string;
};

export type DataVizStandard = {
  key: string;
  name: string;
  chart_type?: string;
  description: string;
  decision_led: boolean;
};

export type AccessibilityRequirement = {
  key: string;
  name: string;
  standard?: string;
  mandatory: boolean;
};

export type MotionRule = {
  key: string;
  name: string;
  duration?: string;
  description: string;
  purpose: string;
};

export type MobilePriority = {
  key: string;
  name: string;
  description: string;
  priority: number;
};

export type DesignToken = {
  key: string;
  category: string;
  name: string;
  value: string;
};

export type ImplementationStandard = {
  key: string;
  name: string;
  description: string;
  required: boolean;
};

export type EDSDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  applies_to: string[];
  mission_attributes: number;
  philosophy_principles: number;
  visual_identity_rules: number;
  components: number;
  workspace_regions: number;
  navigation_patterns: number;
  ai_experience_patterns: number;
  data_viz_standards: number;
  accessibility_rules: number;
  motion_rules: number;
  design_tokens: number;
  generated_at: string;
};

export type ScreenComplianceValidation = {
  screen_key: string;
  valid: boolean;
  uses_design_tokens: boolean;
  uses_component_library: boolean;
  no_custom_patterns: boolean;
  accessible: boolean;
  responsive: boolean;
  dark_mode_support: boolean;
  action_first: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type EDSBibleStatus = {
  bible_id: string;
  version: string;
  applies_to: string[];
  mission_attributes: number;
  philosophy_principles: number;
  visual_identity_rules: number;
  components: number;
  workspace_regions: number;
  navigation_patterns: number;
  ai_experience_patterns: number;
  data_viz_standards: number;
  accessibility_rules: number;
  motion_rules: number;
  design_tokens: number;
  single_visual_language: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const enterpriseDesignSystemApi = {
  dashboard: () => fetchGovernance<EDSDashboard>('/eds/dashboard'),
  mission: () => fetchGovernance<{ attributes: MissionAttribute[] }>('/eds/mission').then((r) => r.attributes),
  philosophy: () => fetchGovernance<{ principles: PhilosophyPrinciple[] }>('/eds/philosophy').then((r) => r.principles),
  visualIdentity: () => fetchGovernance<{ rules: VisualIdentityRule[] }>('/eds/visual-identity').then((r) => r.rules),
  components: () => fetchGovernance<{ components: ComponentLibraryEntry[] }>('/eds/components').then((r) => r.components),
  dashboardPhilosophy: () => fetchGovernance<{ rules: DashboardPhilosophyRule[] }>('/eds/dashboard-philosophy').then((r) => r.rules),
  productWorkspace: () => fetchGovernance<{ regions: WorkspaceRegion[] }>('/eds/product-workspace').then((r) => r.regions),
  navigation: () => fetchGovernance<{ patterns: NavigationPattern[] }>('/eds/navigation').then((r) => r.patterns),
  aiExperience: () => fetchGovernance<{ patterns: AIExperiencePattern[] }>('/eds/ai-experience').then((r) => r.patterns),
  dataVisualization: () => fetchGovernance<{ standards: DataVizStandard[] }>('/eds/data-visualization').then((r) => r.standards),
  accessibility: () => fetchGovernance<{ requirements: AccessibilityRequirement[] }>('/eds/accessibility').then((r) => r.requirements),
  motionDesign: () => fetchGovernance<{ rules: MotionRule[] }>('/eds/motion-design').then((r) => r.rules),
  mobileExperience: () => fetchGovernance<{ priorities: MobilePriority[] }>('/eds/mobile-experience').then((r) => r.priorities),
  designTokens: () => fetchGovernance<{ tokens: DesignToken[] }>('/eds/design-tokens').then((r) => r.tokens),
  implementationStandards: () => fetchGovernance<{ standards: ImplementationStandard[] }>('/eds/implementation-standards').then((r) => r.standards),
  validateScreen: (payload: { screen_key: string; attrs: Record<string, boolean> }) =>
    fetchGovernance<ScreenComplianceValidation>('/eds/validate-screen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchGovernance<EDSBibleStatus>('/eds/status'),
};
